<template>
  <div class="configuration-form">
    <h3 class="text-xl font-semibold mb-4">
      {{ configuration ? 'Edit Configuration' : 'New Configuration' }}
    </h3>

    <form @submit.prevent="handleSubmit">
      <!-- Foundation Campaign Selection -->
      <div class="form-group mb-4">
        <label class="block text-sm font-medium mb-2">
          Foundation Campaign <span class="text-red-500">*</span>
        </label>
        <select 
          v-model="form.foundation_campaign_id"
          class="form-select w-full"
          :disabled="!!configuration"
          required
        >
          <option value="">Select a foundation campaign...</option>
          <option 
            v-for="foundation in availableFoundations" 
            :key="foundation.id"
            :value="String(foundation.id)"
          >
            {{ foundation.name }}
          </option>
        </select>
        <p v-if="errors.foundation_campaign_id" class="text-red-500 text-sm mt-1">
          {{ errors.foundation_campaign_id }}
        </p>
      </div>

      <!-- Enable/Disable Toggle -->
      <div class="form-group mb-4">
        <label class="flex items-center">
          <input 
            type="checkbox"
            v-model="form.enabled"
            class="mr-2"
          >
          <span class="text-sm font-medium">Enable synchronization</span>
        </label>
        <p class="text-gray-600 text-xs mt-1">
          When enabled, new campaigns under this foundation will automatically sync to Salesforce
        </p>
      </div>

      <!-- Template Selection (for new configurations) -->
      <div v-if="!configuration && templates.length > 0" class="form-group mb-4">
        <label class="block text-sm font-medium mb-2">
          Use Template (Optional)
        </label>
        <select 
          v-model="form.template_id"
          class="form-select w-full"
          @change="onTemplateChange"
        >
          <option value="">No template</option>
          <option 
            v-for="template in templates" 
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </select>
        <p v-if="form.template_id" class="text-gray-600 text-xs mt-1">
          This will pre-fill field mappings from the selected template
        </p>
      </div>

      <!-- Connection Settings -->
      <div class="form-group mb-6">
        <h4 class="text-lg font-medium mb-3">Connection Settings</h4>
        
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium mb-2">
              Sync Mode
            </label>
            <select 
              v-model="connectionSettings.sync_mode"
              class="form-select w-full"
            >
              <option value="create_only">Create Only</option>
              <option value="create_update">Create or Update</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Duplicate Check Field
            </label>
            <select 
              v-model="connectionSettings.duplicate_check_field"
              class="form-select w-full"
            >
              <option value="email">Email</option>
              <option value="email_and_name">Email + Name</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Use Assignment Rules
            </label>
            <label class="flex items-center">
              <input 
                type="checkbox"
                v-model="connectionSettings.use_assignment_rules"
                class="mr-2"
              >
              <span class="text-sm">Enable Salesforce assignment rules</span>
            </label>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Dry Run Mode
            </label>
            <label class="flex items-center">
              <input 
                type="checkbox"
                v-model="connectionSettings.dry_run"
                class="mr-2"
              >
              <span class="text-sm">Test without creating records</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Sync Settings -->
      <div class="form-group mb-6">
        <h4 class="text-lg font-medium mb-3">Sync Settings</h4>
        
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium mb-2">
              Batch Size
            </label>
            <input 
              type="number"
              v-model.number="syncSettings.batch_size"
              class="form-input w-full"
              min="1"
              max="200"
            >
            <p class="text-gray-600 text-xs mt-1">
              Number of records to sync per batch (max 200)
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Retry Attempts
            </label>
            <input 
              type="number"
              v-model.number="syncSettings.retry_attempts"
              class="form-input w-full"
              min="0"
              max="5"
            >
            <p class="text-gray-600 text-xs mt-1">
              Number of retry attempts for failed syncs
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Retry Delay (seconds)
            </label>
            <input 
              type="number"
              v-model.number="syncSettings.retry_delay"
              class="form-input w-full"
              min="10"
              max="300"
            >
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              Log Level
            </label>
            <select 
              v-model="syncSettings.log_level"
              class="form-select w-full"
            >
              <option value="error">Errors Only</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Field Mappings -->
      <div class="form-group mb-6">
        <h4 class="text-lg font-medium mb-3">Field Mappings</h4>
        <FieldMappingEditor
          v-model="form.field_mappings"
          :templates="templates"
        />
      </div>

      <!-- Form Actions -->
      <div class="flex justify-end space-x-3">
        <button 
          type="button"
          @click="$emit('cancel')"
          class="btn btn-secondary"
        >
          Cancel
        </button>
        
        <button 
          type="button"
          @click="testConfiguration"
          class="btn btn-info"
          :disabled="!form.foundation_campaign_id || testing"
        >
          <i v-if="testing" class="fas fa-spinner fa-spin mr-2"></i>
          <i v-else class="fas fa-plug mr-2"></i>
          Test Connection
        </button>
        
        <button 
          type="submit"
          class="btn btn-primary"
          :disabled="saving || !form.foundation_campaign_id"
        >
          <i v-if="saving" class="fas fa-spinner fa-spin mr-2"></i>
          <i v-else class="fas fa-save mr-2"></i>
          {{ configuration ? 'Update' : 'Create' }} Configuration
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import FieldMappingEditor from './FieldMappingEditor.vue';

export default {
  name: 'ConfigurationForm',
  components: {
    FieldMappingEditor,
  },
  props: {
    configuration: {
      type: Object,
      default: null,
    },
    foundations: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const form = ref({
      foundation_campaign_id: '',
      enabled: false,
      template_id: '',
      field_mappings: [],
    });

    const connectionSettings = ref({
      sync_mode: 'create_update',
      duplicate_check_field: 'email',
      use_assignment_rules: true,
      dry_run: false,
    });

    const syncSettings = ref({
      batch_size: 50,
      retry_attempts: 3,
      retry_delay: 60,
      log_level: 'info',
    });

    const errors = ref({});
    const saving = ref(false);
    const testing = ref(false);

    // Debug foundations prop
    onMounted(() => {
      console.log('ConfigurationForm mounted with foundations:', props.foundations);
      console.log('Foundations length:', props.foundations?.length);
      if (props.foundations?.length > 0) {
        console.log('First foundation:', props.foundations[0]);
      }
    });
    
    watch(() => props.foundations, (newVal) => {
      console.log('Foundations updated:', newVal);
      console.log('Foundations is array?', Array.isArray(newVal));
      console.log('Foundations length in watcher:', newVal?.length);
    }, { immediate: true, deep: true });

    // Get available foundations
    const availableFoundations = computed(() => {
      console.log('Computing availableFoundations, props.foundations:', props.foundations);
      console.log('props.configuration:', props.configuration);
      
      // Ensure we have an array
      const foundationsList = Array.isArray(props.foundations) ? props.foundations : [];
      console.log('Foundations list length:', foundationsList.length);
      
      // In edit mode, the dropdown is disabled anyway, so we can show all foundations
      // This ensures the current foundation is visible even if it already has a configuration
      if (props.configuration && props.configuration.id) {
        console.log('Edit mode - showing all foundations for disabled dropdown');
        console.log('Current foundation_campaign_id:', props.configuration.foundation_campaign_id);
        return foundationsList;
      }
      
      // In create mode, we should filter out foundations that already have configurations
      // For now, return all foundations (TODO: implement filtering)
      console.log('Create mode, returning all foundations:', foundationsList);
      return foundationsList;
    });

    const onTemplateChange = () => {
      if (form.value.template_id) {
        const template = props.templates.find(t => t.id === form.value.template_id);
        if (template && template.mappings) {
          // Apply template mappings
          form.value.field_mappings = JSON.parse(template.mappings);
        }
      }
    };

    const testConfiguration = async () => {
      testing.value = true;
      try {
        const response = await axios.post('/admin/salesforce/test-connection', {
          configuration_id: props.configuration?.id,
          foundation_campaign_id: form.value.foundation_campaign_id,
          connection_settings: connectionSettings.value,
        });

        if (response.data.success) {
          alert('Connection test successful!');
        } else {
          alert(`Connection test failed: ${response.data.message}`);
        }
      } catch (error) {
        console.error('Connection test error:', error);
        alert('Connection test failed. Please check your settings.');
      } finally {
        testing.value = false;
      }
    };

    const handleSubmit = async () => {
      errors.value = {};
      
      if (!form.value.foundation_campaign_id) {
        errors.value.foundation_campaign_id = 'Foundation campaign is required';
        return;
      }

      saving.value = true;
      
      const payload = {
        foundation_campaign_id: form.value.foundation_campaign_id,
        enabled: form.value.enabled,
        connection_settings: connectionSettings.value,
        sync_settings: syncSettings.value,
        field_mappings: form.value.field_mappings,
      };

      if (!props.configuration && form.value.template_id) {
        payload.template_id = form.value.template_id;
      }

      emit('save', payload);
      saving.value = false;
    };

    // Initialize form with existing configuration
    onMounted(() => {
      console.log('ConfigurationForm onMounted, configuration:', props.configuration);
      console.log('ConfigurationForm onMounted, foundations:', props.foundations);
      
      if (props.configuration) {
        // The foundation_campaign_id should match the option values which are now strings
        const foundationId = props.configuration.foundation_campaign_id ? String(props.configuration.foundation_campaign_id) : '';
        console.log('Initializing form with foundation_campaign_id:', foundationId, 'type:', typeof foundationId);
        
        form.value = {
          foundation_campaign_id: foundationId,
          enabled: props.configuration.enabled,
          template_id: '',
          field_mappings: props.configuration.field_mappings || [],
        };

        if (props.configuration.connection_settings) {
          connectionSettings.value = {
            ...connectionSettings.value,
            ...props.configuration.connection_settings,
          };
        }

        if (props.configuration.sync_settings) {
          syncSettings.value = {
            ...syncSettings.value,
            ...props.configuration.sync_settings,
          };
        }
        
        console.log('Form initialized, form.foundation_campaign_id:', form.value.foundation_campaign_id);
        console.log('Available foundations count:', props.foundations?.length);
      }
    });

    return {
      form,
      connectionSettings,
      syncSettings,
      errors,
      saving,
      testing,
      availableFoundations,
      onTemplateChange,
      testConfiguration,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.form-select,
.form-input {
  @apply border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600;
}

.btn-secondary {
  @apply bg-gray-500 text-white hover:bg-gray-600;
}

.btn-info {
  @apply bg-cyan-500 text-white hover:bg-cyan-600;
}

.btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>